import Page from 'components/menu/page.component';
import TableSortable, { Column, Row, RowObject } from 'components/menu/tableSortable';
import GroupSetting from 'components/settings/groupSetting.component';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { EditPenSVG } from 'images/editPen.svg';
import InputNumber from 'components/inputs/inputNumber.component';
import { Slider, SliderRange } from 'components/inputs/slider';
import { TaskAction, TaskActionPost, TaskActionPut } from 'interfaces/taskAction.interface';
import { SectorSettings, Setting } from 'interfaces/setting.interface';
import { configService, LoggerService, RootState, usePersistantState } from '@umanforge/common';
import { useSelector, useDispatch } from 'react-redux';
import { SettingService } from 'services/setting.service';
import { LocationService } from 'services/location.service';
import { TaskActionService } from 'services/taskAction.service';
import { Location, LocationPost, LocationPut } from 'interfaces/location.interface';
import { KEY } from 'constants/local_storage_key.constant';
import LocationModal from 'components/modals/location/locationModal.component';
import TaskActionModal from 'components/modals/task_action/taskActionModal.component';
import { DEFAULT_VALUES } from 'constants/calendar.constants';
import ConfirmationModal from '@common_components/modals/confirmationModal.component';
import { TrashIcon } from '@common_components/icons/trash.icon';
import { ToastService } from 'services/toast.service';
import ImageOnError from 'components/other/ImageOnError';


interface TableButtonsProps {
  modifyHandle: () => void;
  deleteHandle: () => void;
  isDeleteDisabled?: boolean;
  deleteTooltip?: string;
  disabledTooltip?: string;
  className?: string;
}

export const TableButtons: React.FC<TableButtonsProps> = ( {
  modifyHandle,
  deleteHandle,
  isDeleteDisabled = false,
  deleteTooltip = 'Supprimer cet élément',
  disabledTooltip = 'Cet élément est utilisé et ne peut pas être supprimé',
  className = ''
} ) => {
  return (
    <div className={`absolute w-full inline-flex ${className}`}>
      <div className="text-sm pl-2 py-l w-full"> </div>
      <button className='m-0.5 -mb-0.5' onClick={modifyHandle}>
        <div className="w-full inline-flex justify-center bg-white/60 hover:bg-white/90 hover:outline-1 hover:outline hover:outline-slate-400 rounded-lg">
          <EditPenSVG width={20} stroke="#3388EE" className="hover:fill-blue-500"/>
        </div>
      </button>
      <button
        className={`m-0.5 -mb-0.5 ${isDeleteDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
        onClick={isDeleteDisabled ? undefined : deleteHandle}
        disabled={isDeleteDisabled}
        title={isDeleteDisabled ? disabledTooltip : deleteTooltip}
      >
        <div className={`w-full inline-flex justify-center rounded-lg ${
          isDeleteDisabled
            ? 'bg-gray-200 cursor-not-allowed'
            : 'bg-white/60 hover:bg-white/90 hover:outline-1 hover:outline hover:outline-slate-400'
        }`}>
          <TrashIcon
            size={5}
            className={isDeleteDisabled ? 'text-gray-400' : 'hover:text-red-500 text-[#EE3333]'}
          />
        </div>
      </button>
    </div>
  );
};


function SettingPage() {
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const toastService = new ToastService( 'SettingPage' );
  const logger: LoggerService = new LoggerService( 'Setting page' );

  const [ selectedSectorId, setSelectedSectorId ] = usePersistantState<string | undefined>( KEY.SELECTED_SECTOR_ID, undefined );
  const [ locations, setLocations ] = useState<Location[]>( [] );
  const [ taskActions, setTaskActions ] = useState<TaskAction[]>( [] );
  const [ settings, setSettings ] = useState<SectorSettings>( );

  const [ referencedLocationIds, setReferencedLocationIds ] = useState<Set<string>>( new Set() );
  const [ referencedTaskActionIds, setReferencedTaskActionIds ] = useState<Set<string>>( new Set() );

  const [ isFirstRender, setIsFirstRender ] = useState( true );
  const [ isDeleteTaskActionModalOpen, setIsDeleteTaskActionModalOpen ] = useState( false );
  const [ isDeleteLocationModalOpen, setIsDeleteLocationModalOpen ] = useState( false );

  const [ locationModal, setLocationModal ] = useState( false );
  const [ taskActionModal, setTaskActionModal ] = useState( false );
  const [ selectedLocationId, setSelectedLocationId ] = useState<string>( );
  const [ selectedTaskActionId, setSelectedTaskActionId ] = useState<string>( );

  const [ backendBaseUrl, setBackendBaseUrl ] = useState<string>( '' );

  const fetchSectorData = async ( sectorId: string | undefined = selectedSectorId ) => {
    if ( sectorId && sectorId != undefined ) {
      try {
        const [ _locations, _taskActions, _settings ] = await Promise.all( [
          LocationService.getLocationBySector( sectorId, token, dispatch ),
          TaskActionService.getTaskActionBySector( sectorId, token, dispatch ),
          SettingService.getSectorSettingsBySector( sectorId, token, dispatch )
        ] );

        setLocations( _locations );
        setTaskActions( _taskActions );
        setSettings( _settings );

        await checkLocationUsage( _locations );
        await checkTaskActionUsage( _taskActions );
      }
      catch ( error ) {
        console.error( 'error fetch data', error );
      }
    }
  };

  const checkLocationUsage = async ( locationsToCheck: Location[] ) => {
    if ( !locationsToCheck.length ) {
      return;
    }

    try {
      const usageChecks = await Promise.all(
        locationsToCheck.map( async location => {
          try {
            const result = await LocationService.checkLocationUsage( location.id, token, dispatch );
            return { id: location.id, isUsed: result.isUsed };
          } catch ( error ) {
            console.warn( `Failed to check usage for location ${location.id}:`, error );
            return { id: location.id, isUsed: false };
          }
        } )
      );

      const usedLocationIds = new Set(
        usageChecks.filter( check => { return check.isUsed; } ).map( check => {
          return check.id;
        } )
      );
      setReferencedLocationIds( usedLocationIds );
    } catch ( error ) {
      console.error( 'Error checking location usage:', error );
    }
  };

  const checkTaskActionUsage = async ( taskActionsToCheck: TaskAction[] ) => {
    if ( !taskActionsToCheck.length ) {
      return;
    }

    try {
      const usageChecks = await Promise.all(
        taskActionsToCheck.map( async taskAction => {
          try {
            const result = await TaskActionService.checkTaskActionUsage( taskAction.id, token, dispatch );
            return { id: taskAction.id, isUsed: result.isUsed };
          } catch ( error ) {
            console.warn( `Failed to check usage for task action ${taskAction.id}:`, error );
            return { id: taskAction.id, isUsed: false };
          }
        } )
      );

      const usedTaskActionIds = new Set(
        usageChecks.filter( check => { return check.isUsed; } ).map( check => {
          return check.id;
        } )
      );
      setReferencedTaskActionIds( usedTaskActionIds );
    } catch ( error ) {
      console.error( 'Error checking task action usage:', error );
    }
  };

  const fetchLocation = async () => {
    if ( selectedSectorId ) {
      const _locations = await LocationService.getLocationBySector( selectedSectorId, token, dispatch );
      if ( _locations ) {
        setLocations( _locations );
        await checkLocationUsage( _locations );
      }
    }
  };
  const fetchTaskAction = async () => {
    if ( selectedSectorId ) {
      const _taskActions = await TaskActionService.getTaskActionBySector( selectedSectorId, token, dispatch );
      if ( _taskActions ) {
        setTaskActions( _taskActions );
        await checkTaskActionUsage( _taskActions );
      }
    }
  };

  const fetchSettings = async () => {
    if ( selectedSectorId ) {
      const _settings = await SettingService.getSectorSettingsBySector( selectedSectorId, token, dispatch );
      if ( _settings ) {
        setSettings( _settings );
      }
    }
  };

  const updateSetting = async ( setting: Setting ) => {
    if ( setting.id == null ) {
      logger.warn( 'setting do not have id ! trying to re-fetch settings.' );
      fetchSettings();
    }
    else if ( !isFirstRender ) {
      await SettingService.putSetting(
        setting.id,
        setting,
        token,
        dispatch,
        fetchSettings
      );
      fetchSettings();
    }
  };

  useEffect( () => {
    const init = async () => {
      await configService.getBackendUrl().then( url => { setBackendBaseUrl( url ); } );
      setIsFirstRender( false );
    };
    init();
  }, [] );

  useEffect( () => {
    if ( !isFirstRender && selectedSectorId != null ) {
      fetchSectorData( selectedSectorId );
    }
  }, [ selectedSectorId, token, isFirstRender ] );


  const onSectorsChange = ( sectorId: string | undefined ) => {
    setSelectedSectorId( sectorId );
  };


  const closeTaskActionModal = () => {
    fetchTaskAction();
    setSelectedTaskActionId( undefined );
    setTaskActionModal( false );
  };

  const handleTaskActionUpdate = async ( taskAction: TaskActionPut ) => {
    await TaskActionService.putTaskAction( {
      id: taskAction.id,
      name: taskAction.name,
      icon: taskAction.icon
    } as TaskActionPut, token, dispatch );
    closeTaskActionModal();
  };

  const handleTaskActionCreate = async ( taskAction: TaskActionPost ) => {
    await TaskActionService.postTaskAction( {
      name: taskAction.name,
      icon: taskAction.icon,
      idSector: selectedSectorId
    } as TaskActionPost, token, dispatch );
    closeTaskActionModal();
  };

  const handleTaskActionDelete = async ( taskActionId: string ) => {
    try {
      await TaskActionService.deleteTaskAction( taskActionId, token, dispatch );
      closeTaskActionModal();
      toastService.showSuccess( 'translation:task_action_deleted' );
    } catch ( error: unknown ) {
      let errorHandled = false;

      if ( error && typeof error === 'object' && 'response' in error ) {
        const axiosError = error as { response?: { data?: { err?: number } } };
        const errorCode = axiosError.response?.data?.err;

        if ( errorCode === 2037 ) {
          setReferencedTaskActionIds( prev => {
            const newSet = new Set( prev );
            newSet.add( taskActionId );
            return newSet;
          } );
          toastService.showError( 'translation:errors.task_action_already_used' );
          errorHandled = true;
        }
      }

      if ( !errorHandled ) {
        const errorMessage = String( error );
        if ( errorMessage.includes( 'ER_ROW_IS_REFERENCED_2' ) ||
             errorMessage.includes( 'foreign key constraint fails' ) ||
             errorMessage.includes( 'FOREIGN KEY constraint failed' ) ||
             errorMessage.includes( 'cannot delete or update a parent row' ) ||
             errorMessage.includes( 'ForeignKeyConstraintError' ) ||
             errorMessage.includes( 'it is referenced by existing tasks' ) ||
             errorMessage.includes( 'Task action is already being used' ) ) {
          setReferencedTaskActionIds( prev => {
            const newSet = new Set( prev );
            newSet.add( taskActionId );
            return newSet;
          } );
          toastService.showError( 'translation:errors.task_action_already_used' );
        } else {
          toastService.showError( 'translation:errors.delete_error_generic' );
        }
      }

      setIsDeleteTaskActionModalOpen( false );
      setSelectedTaskActionId( undefined );
    }
  };

  const closeLocationModal = () => {
    fetchLocation();
    setSelectedLocationId( undefined );
    setLocationModal( false );
  };

  const handleLocationUpdate = async ( location: Location ) => {
    await LocationService.putLocation( {
      id: location.id,
      name: location.name
    } as LocationPut, token, dispatch );
    closeLocationModal();
  };

  const handleLocationCreate = async ( location: Location ) => {
    await LocationService.postLocation( {
      name: location.name,
      idSector: selectedSectorId
    } as LocationPost, token, dispatch );
    closeLocationModal();
  };

  const handleLocationDelete = async ( locationId: string ) => {
    try {
      await LocationService.deleteLocation( ( locationId ?? selectedLocationId )!, token, dispatch );
      closeLocationModal();
      toastService.showSuccess( 'translation:location_deleted' );
    } catch ( error: unknown ) {
      let errorHandled = false;

      if ( error && typeof error === 'object' && 'response' in error ) {
        const axiosError = error as { response?: { data?: { err?: number } } };
        const errorCode = axiosError.response?.data?.err;

        if ( errorCode === 2036 ) {
          setReferencedLocationIds( prev => {
            const newSet = new Set( prev );
            newSet.add( locationId ?? selectedLocationId! );
            return newSet;
          } );
          toastService.showError( 'translation:errors.location_already_used' );
          errorHandled = true;
        }
      }

      if ( !errorHandled ) {
        const errorMessage = String( error );
        if ( errorMessage.includes( 'ER_ROW_IS_REFERENCED_2' ) ||
             errorMessage.includes( 'foreign key constraint fails' ) ||
             errorMessage.includes( 'FOREIGN KEY constraint failed' ) ||
             errorMessage.includes( 'cannot delete or update a parent row' ) ||
             errorMessage.includes( 'ForeignKeyConstraintError' ) ||
             errorMessage.includes( 'it is referenced by existing tasks' ) ||
             errorMessage.includes( 'Location is already being used' ) ) {
          setReferencedLocationIds( prev => {
            const newSet = new Set( prev );
            newSet.add( locationId ?? selectedLocationId! );
            return newSet;
          } );
          toastService.showError( 'translation:errors.location_already_used' );
        } else {
          toastService.showError( 'translation:errors.delete_error_generic' );
        }
      }

      setIsDeleteLocationModalOpen( false );
      setSelectedLocationId( undefined );
    }
  };


  const locationTemplate = ( rowElement: string, object: RowObject, index?: number ): React.ReactElement => {
    if ( index === 0 ) {
      return <div className="flex items-center justify-center h-full text-center min-h-[32px]">
        <span className={ 'm-auto truncate' } title={object.name}>{object.name}</span>
      </div>;
    }
    else if ( index === 1 ) {
      return (
        <div className="relative h-full" style={{ minHeight: '32px' }}>
          <TableButtons
            modifyHandle={() => {
              setSelectedLocationId( object.id );
              setLocationModal( true );
            }}
            deleteHandle={() => {
              if ( !object.isDeletable ) {
                setSelectedLocationId( object.id );
                setIsDeleteLocationModalOpen( true );
              }
            }}
            isDeleteDisabled={object.isDeletable}
            deleteTooltip={t( 'translation:delete_element' )}
            disabledTooltip={t( 'translation:element_is_used_cannot_be_deleted' )}
          />
        </div>
      );
    }
    return <span>{object.name}</span>;
  };

  const taskActionTemplate = ( rowElement: string, object: RowObject, index?: number ): React.ReactElement => {
    if ( index === 0 ) {
      return getIcon( object.icon, object.name );
    }
    else if ( index === 1 ) {
      return (
        <div className="flex items-center justify-center h-full text-center min-h-[32px]">
          <span className={ 'm-auto truncate' } title={object.name}>{object.name}</span>
        </div>
      );
    }
    else if ( index === 2 ) {
      return (
        <div className="relative h-full" style={{ minHeight: '32px' }}>
          <TableButtons
            modifyHandle={() => {
              setSelectedTaskActionId( object.id );
              setTaskActionModal( true );
            }}
            deleteHandle={() => {
              if ( !object.isDeletable ) {
                setSelectedTaskActionId( object.id );
                setIsDeleteTaskActionModalOpen( true );
              }
            }}
            isDeleteDisabled={object.isDeletable}
            deleteTooltip={t( 'translation:delete_element' )}
            disabledTooltip={t( 'translation:element_is_used_cannot_be_deleted' )}
          />
        </div>
      );
    }
    return <span>{object.name}</span>;
  };

  const getIcon = ( iconId: string | undefined, name: string ) => {
    if ( iconId ) {

      // TEMP compatibilité Base64 ( ancien systeme )
      try {
        if ( typeof iconId !== 'string' || iconId.trim() === '' ) {
          throw new Error( 'Invalid icon data' );
        }
        const cleanIcon = iconId.replace( /\s/g, '' );
        const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
        if ( base64Regex.test( cleanIcon ) && cleanIcon.length > 10 ) {
          try {
            const decodedContent = atob( cleanIcon );
            if ( decodedContent.includes( '<svg' ) ) {
              const svgHtml = decodedContent.replace(
                /<svg([^>]*)>/,
                ( match, attrs ) => {
                  const newAttrs = attrs.replace( /\swidth="[^"]*"/g, '' ).replace( /\sheight="[^"]*"/g, '' );
                  return `<svg${newAttrs} width="42" height="42">`;
                }
              );
              return (
                <div className="flex items-center justify-center h-full" style={{ minHeight: '32px', textAlign: 'center' }}>
                  <span
                    dangerouslySetInnerHTML={{ __html: svgHtml }}
                    style={{ display: 'flex', width: '64px', height: '64px', alignItems: 'center', justifyContent: 'center', margin: 'auto' }}
                  />
                </div>
              );
            }
          } catch {
            // Not SVG or decode failed, treat as image
          }
          let mimeType = 'image/jpeg';
          if ( cleanIcon.startsWith( '/9j/' ) ) {
            mimeType = 'image/jpeg';
          } else if ( cleanIcon.startsWith( 'iVBOR' ) ) {
            mimeType = 'image/png';
          } else if ( cleanIcon.startsWith( 'R0lGOD' ) ) {
            mimeType = 'image/gif';
          }
          return (
            <div className="flex items-center justify-center h-full" style={{ minHeight: '32px', textAlign: 'center' }}>
              <ImageOnError src={`data:${mimeType};base64,${cleanIcon}`} alt={name} className='w-[42px] aspect-square p-0.5 z-10' />
            </div>
          );
        } else {
          let imageUrl = cleanIcon;
          if ( backendBaseUrl ) {
            imageUrl = `${backendBaseUrl}/${imageUrl.replace( /^\/+/, '' )}`;
          }
          return (
            <div className="flex items-center justify-center h-full" style={{ minHeight: '32px', textAlign: 'center' }}>
              <ImageOnError src={ imageUrl} alt={name} className='w-[42px] aspect-square p-0.5 z-10' />
            </div>
          );
        }
      } catch {
        return (
          <div className="flex items-center justify-center h-full" style={{ minHeight: '32px', textAlign: 'center' }}>
            <span style={{ margin: 'auto', fontSize: '12px', color: '#666' }}>
              {name?.substring( 0, 2 ).toUpperCase() || '??'}
            </span>
          </div>
        );
      }
    } else {
      return (
        <div className="flex items-center justify-center h-full" style={{ minHeight: '32px', textAlign: 'center' }}>
          <span style={{ margin: 'auto', fontSize: '12px', color: '#666' }}>
            {name?.substring( 0, 2 ).toUpperCase() || '??'}
          </span>
        </div>
      );
    }
  };

  const locationRows = useMemo( () => {
    return locations.map( location => { return new Row( { id: location.id, name: location.name, isDeletable: referencedLocationIds.has( location.id ) }, locationTemplate ); } );
  }, [ locations, referencedLocationIds, backendBaseUrl ] );


  const taskActionRows = useMemo( () => {
    return taskActions.map( taskAction => {
      const rowData = {
        id: taskAction.id,
        name: taskAction.name,
        icon: typeof taskAction.icon === 'string' ? taskAction.icon : '',
        isDeletable: referencedTaskActionIds.has( taskAction.id )
      };

      return new Row( rowData, taskActionTemplate );
    } );
  }, [ taskActions, referencedTaskActionIds, backendBaseUrl ] );


  return (
    <Page
      title={t( 'translation:sector_settings' )}
      isAdminRequired={ true }
      onSectorChange={onSectorsChange}
    >
      <>
        <div className='lg:flex lg:space-x-10 mx-3'>
          <GroupSetting title={ `${t( 'translation:calendar' )} / ${ t ( 'translation:chronogram' )}` }>

            <div className='setting-tile'>
              <div className="tile-background"/>
              <div className={'flex gap-2 relative items-center w-1/2'}>
                <label className={'text-gray-600 truncate flex items-center text-pretty'}>
                  { t( 'translation:calendar_time_range' ) }
                </label>
              </div>
              <div className='w-1/2 mx-2'>
                <SliderRange
                  min={0}
                  max={24}
                  valueStart={ settings?.calendarStartHour?.value ?? DEFAULT_VALUES.HOURSTART }
                  valueEnd={ settings?.calendarEndHour?.value ?? DEFAULT_VALUES.HOUREND }
                  onChangeStart={ ( value: number ) => {
                    updateSetting( { ...settings?.calendarStartHour, value: value } as Setting );
                  }}
                  onChangeEnd={ ( value: number ) => {
                    updateSetting( { ...settings?.calendarEndHour, value: value } as Setting );
                  }}
                  step={1}
                  visualStep={6}
                  unit={'h'}
                />
              </div>
            </div>

            <div className='setting-tile'>
              <div className="tile-background"/>
              <div className={'flex gap-2 relative items-center w-1/2'}>
                <label className={'text-gray-600 truncate flex items-center text-pretty'}>
                  { t( 'translation:calendar_tick' ) }
                </label>
              </div>
              <div className='w-1/2 mx-2 mt-1.5'>
                <Slider
                  options={ [ 15, 30, 60 ] }
                  value={ settings?.calendarStep?.value ?? DEFAULT_VALUES.STEP }
                  onChange={ ( value: number ) => {
                    updateSetting( { ...settings?.calendarStep, value: value } as Setting );
                  }}
                  unit={'min'}
                />
              </div>
            </div>

            <div className='setting-tile'>
              <div className="tile-background"/>
              <div className={'flex gap-2 relative items-center w-1/2'}>
                <label className={'text-gray-600 truncate flex items-center text-pretty'}>
                  { t( 'translation:show_weekend' ) }
                </label>
              </div>
              <div className="pt-2 w-fit flex items-center borber border-red-500">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    value=""
                    className="sr-only peer"
                    onChange={ ( e: React.ChangeEvent<HTMLInputElement> ) => {
                      const value = e.target?.checked ? 1 : 0;
                      updateSetting( { ...settings?.showWeekend, value: value } as Setting );
                    }}
                    checked={ ( settings?.showWeekend?.value ?? DEFAULT_VALUES.NUMBER_MONTH_DELETE_IMAGE ) == 1 }
                  />
                  <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all  peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </GroupSetting>

          <GroupSetting title={ t( 'common:sector' )}>
            <div className='setting-tile'>
              <div className="tile-background"/>
              <InputNumber
                label={`${ t( 'translation:delete_image' ) }`}
                textSize='w-full'
                labelClassName='text-pretty'
                max={36}
                min={6}
                step={6}
                value={ settings?.numberMonthDeleteImage?.value ?? DEFAULT_VALUES.NUMBER_MONTH_DELETE_IMAGE }
                onChange={( value: number ) => {
                  updateSetting( { ...settings?.numberMonthDeleteImage, value: value } as Setting );
                }}
              />
            </div>


            <div className='setting-tile'>
              <div className="tile-background"/>
              <InputNumber
                label={`${ t( 'translation:close_action_time' )}`}
                textSize='w-full'
                labelClassName='text-pretty'
                max={240}
                min={20}
                step={20}
                value={ settings?.numberMinuteCloseAction?.value ?? DEFAULT_VALUES.NUMBER_MINUTE_CLOSE_ACTION }
                onChange={( value: number ) => {
                  updateSetting( { ...settings?.numberMinuteCloseAction, value: value } as Setting );
                }}
              />
            </div>
            <></>
          </GroupSetting>
        </div>

        <div className='visible w-[64%] mx-[18%] my-4 bg-blue-100 rounded h-0.5' />

        <div className='xl:flex'>
          <div className='xl:w-1/2 xl:pr-4 xl:max-w-1/2'>
            <TableSortable
              buttonTitle={ t( 'translation:locations' ) }
              cols={[
                new Column( t( 'translation:name' ), 'name', 3 ),
                new Column( t( 'translation:actions' ), 'actions' )
              ]}
              rows={locationRows}
              addButtonHandle={ () => { setLocationModal( true ); } }
              update={locationRows.map( loc => { return loc._object.id + loc._object.name + loc._object.isDeletable; } ).join( '_' )}
            />
          </div>
          <div className='w-0.5 bg-blue-200 mx-2 rounded max-xl:hidden' style={{ minHeight: '200px' }} />
          <div className='xl:w-1/2 xl:pl-4'>
            <TableSortable
              buttonTitle={ t( 'translation:task' ) }
              cols={[
                new Column( t( 'translation:icon' ), 'icon' ),
                new Column( t( 'translation:name' ), 'name', 2 ),
                new Column( t( 'translation:actions' ), 'actions' )
              ]}
              rows={taskActionRows}
              addButtonHandle={ () => { setTaskActionModal( true ); } }
              update={taskActionRows.map( ta => { return ta._object.id + ta._object.name + ta._object.isDeletable + ta._object.icon; } ).join( '_' )}
            />
          </div>
        </div>


        {/* <TimeGridModal planningId={''} sectorId={''}/> */}

        { locationModal && selectedSectorId &&
          <LocationModal
            locationId={ selectedLocationId }
            planningId={ ''}
            sectorId={ selectedSectorId }
            onClose={ () => {
              setSelectedLocationId( undefined );
              setLocationModal( false );
            } }
            onDeleteLocation={ locationId => {
              handleLocationDelete( locationId );
            } }
            onLocationCreate={ location => {
              handleLocationCreate( location );
            } }
            onUpdate={ location => {
              handleLocationUpdate( location );
            } }
          />
        }

        { taskActionModal && selectedSectorId &&
          <TaskActionModal
            taskActionId={ selectedTaskActionId }
            planningId={ ''}
            sectorId={ selectedSectorId }
            onClose={ () => {
              setSelectedTaskActionId( undefined );
              setTaskActionModal( false );
            } }
            onDeleteTaskAction={ taskActionId => {
              handleTaskActionDelete( taskActionId );
            } }
            onTaskActionCreate={ taskAction => {
              handleTaskActionCreate( taskAction );
            } }
            onUpdate={ taskAction => {
              handleTaskActionUpdate( taskAction );
            } }
          />
        }

        <ConfirmationModal
          isOpen={isDeleteLocationModalOpen}
          onConfirm={ () => {
            if ( selectedLocationId ) {
              handleLocationDelete( selectedLocationId );
            }
            setIsDeleteLocationModalOpen( false );
            setSelectedLocationId( undefined );
          } }
          onCancel={() => {
            setIsDeleteLocationModalOpen( false );
            setSelectedLocationId( undefined );
          }}
          title={ t( 'translation:delete_location' ) }
          description={ t( 'translation:are_you_sure_that_you_want_to_delete_location_it_is_irreversible' ) }
        />

        <ConfirmationModal
          isOpen={isDeleteTaskActionModalOpen}
          onConfirm={ () => {
            if ( selectedTaskActionId ) {
              handleTaskActionDelete( selectedTaskActionId );
            }
            setIsDeleteTaskActionModalOpen( false );
            setSelectedTaskActionId( undefined );
          } }
          onCancel={() => {
            setIsDeleteTaskActionModalOpen( false );
            setSelectedTaskActionId( undefined );
          }}
          title={ t( 'translation:delete_task_action' ) }
          description={ t( 'translation:are_you_sure_that_you_want_to_delete_task_action_it_is_irreversible' ) }
        />
      </>
    </Page>
  );
}

export default SettingPage;